// @ts-nocheck
// app/thank-you.tsx — Editorial thank-you page after lead submission

import { useEffect, useMemo } from 'react';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ArrowLeft, Mail } from 'lucide-react-native';

import { theme, ACCENT, FOREST, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import { EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, Section, Title } from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { trackMetaPixelLead, trackMetaPixelPageView } from '@/utils/metaPixel';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BOLD = theme.fonts.serifBold;
const SERIF_BLACK = theme.fonts.serifBlack;
const SANS = theme.fonts.regular;
const IS_WEB = Platform.OS === 'web';

const getParamString = (v: unknown): string | null => {
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  return null;
};
const safeJsonParse = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
};

export default function ThankYouPage() {
  const router = useRouter();
  const params = useLocalSearchParams();

  const selectedCourses = useMemo(() => {
    const raw = getParamString((params as any).selectedCourses);
    const parsed = safeJsonParse<any[]>(raw, []);
    return Array.isArray(parsed) ? parsed : [];
  }, [params]);

  const selectedCoursesCount = useMemo(() => {
    const raw = getParamString((params as any).matchCount);
    const n = raw ? parseInt(raw, 10) : 0;
    return n > 0 ? n : selectedCourses.length > 0 ? selectedCourses.length : 1;
  }, [params, selectedCourses]);

  const topicText = useMemo(() => {
    return getParamString((params as any).userSummary)?.trim()
      || getParamString((params as any).topic)?.trim()
      || 'התחום שביקשת';
  }, [params]);

  const selectedCourseName = useMemo(() => {
    const fromParam = getParamString((params as any).courseName)?.trim();
    if (fromParam) return fromParam;
    const fromList = selectedCourses?.[0]?.marketing_title || selectedCourses?.[0]?.name;
    return typeof fromList === 'string' && fromList.trim().length > 0 ? fromList.trim() : null;
  }, [params, selectedCourses]);

  useEffect(() => {
    trackMetaPixelPageView();
    trackMetaPixelLead();
  }, []);

  return (
    <Paper>
      {IS_WEB && (
        <Head>
          <title>תודה · Kursi</title>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xxl" px="md" max={1000}>
          <View style={s.stampRow}>
            <View style={s.stamp}>
              <Text style={s.stampNum}>✓</Text>
              <Text style={s.stampText}>נקלט במערכת</Text>
            </View>
          </View>

          <View style={{ height: 32 }} />
          <View style={{ alignItems: 'flex-end' }}>
            <Eyebrow color={ACCENT}>בעניין הבקשה שלכם · גיליון מיוחד</Eyebrow>
            <View style={{ height: 16 }} />
            <Text style={s.bigHead}>
              תודה שכתבתם.{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>אנחנו לוקחים מכאן.</Text>
            </Text>
            <View style={{ height: 18 }} />
            <Text style={s.lede}>
              קיבלנו את הבקשה שלכם בנושא <Text style={{ fontFamily: SERIF_BOLD, color: INK }}>{topicText}</Text>.
              {selectedCourseName ? <> בחרתם להתעניין בקורס <Text style={{ fontFamily: SERIF_BOLD, color: INK }}>{selectedCourseName}</Text>.</> : null}
              {' '}אנחנו יוצרים קשר תוך 24 שעות עם פרטים, הטבות ומסלולי המשך.
            </Text>
          </View>
        </Section>

        <Section py="md" px="md" max={1000}>
          <View style={s.statsRow}>
            <View style={s.statCol}>
              <Numeral size={80}>{String(selectedCoursesCount).padStart(2, '0')}</Numeral>
              <Text style={s.statLabel}>קורסים שבחרתם</Text>
            </View>
            <View style={s.statRule} />
            <View style={s.statCol}>
              <Numeral size={80} color={ACCENT}>24h</Numeral>
              <Text style={s.statLabel}>זמן חזרה מקסימלי</Text>
            </View>
            <View style={s.statRule} />
            <View style={s.statCol}>
              <Numeral size={80}>03</Numeral>
              <Text style={s.statLabel}>צעדים עד הקורס</Text>
            </View>
          </View>
        </Section>

        <Section py="xl" px="md" max={1000}>
          <View style={s.nextBlock}>
            <Eyebrow>הצעדים הבאים</Eyebrow>
            <View style={{ height: 14 }} />
            <Headline level={2}>מה קורה עכשיו?</Headline>
            <View style={{ height: 24 }} />
            {[
              { n: '01', t: 'נקבל החלטה על ההצעה הכי טובה עבורכם', d: 'העורך שלנו עובר על הבקשה ובוחר מה שמתאים — קורס מומלץ, הטבה, או הצעת חלופה.' },
              { n: '02', t: 'נשלח אליכם הודעה עם פרטים מלאים', d: 'כל המידע — מחיר, מועדים, אופציות תשלום, הטבות שזמינות לכם — מסודר במייל אחד.' },
              { n: '03', t: 'אתם בוחרים אם להמשיך', d: 'אם זה מתאים — נחבר אתכם למכללה. אם לא — נמשיך לחפש. אין מחויבות.' },
            ].map((n, i) => (
              <View key={i} style={s.nextRow}>
                <Numeral size={56}>{n.n}</Numeral>
                <View style={{ flex: 1, paddingRight: 20 }}>
                  <Text style={s.nextT}>{n.t}</Text>
                  <View style={{ height: 6 }} />
                  <Text style={s.nextD}>{n.d}</Text>
                </View>
              </View>
            ))}
          </View>
        </Section>

        <Section py="xxl" px="md" max={1000}>
          <View style={s.ctaRow}>
            <EditorialButton label="חזרה לעמוד הראשי" onPress={() => router.replace('/')} variant="primary" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
            <EditorialButton label="המשך לקטלוג" onPress={() => router.push('/catalog')} variant="secondary" size="lg" />
          </View>
          <View style={{ height: 16 }} />
          <Text style={s.helperNote}>
            צריכים לעדכן פרטים? כתבו לנו ב-<Text style={{ fontFamily: SERIF_BOLD, color: INK }}>hello@kursiaii.com</Text>
          </Text>
        </Section>

        <EditorialFooter onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  stampRow: { alignItems: 'flex-end' },
  stamp: {
    transform: [{ rotate: '-4deg' }],
    paddingVertical: 10, paddingHorizontal: 16,
    borderWidth: 2, borderColor: FOREST,
    flexDirection: 'row-reverse', alignItems: 'center', gap: 10,
  } as any,
  stampNum: { fontFamily: SERIF_BLACK, fontSize: 22, color: FOREST },
  stampText: { fontFamily: MONO, fontSize: 11, color: FOREST, letterSpacing: 2, textTransform: 'uppercase' as any },
  bigHead: { fontFamily: SERIF_BLACK, fontSize: IS_WEB ? 76 : 44, color: INK, letterSpacing: -2, lineHeight: IS_WEB ? 80 : 50, textAlign: 'right' },
  lede: { fontFamily: SERIF, fontSize: IS_WEB ? 21 : 17, lineHeight: IS_WEB ? 32 : 28, color: INK_SOFT, textAlign: 'right' },

  statsRow: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap', gap: 0,
    borderWidth: 1, borderColor: INK, padding: 0,
    backgroundColor: PAPER_SOFT,
  } as any,
  statCol: { flex: 1, padding: 28, alignItems: 'flex-end', minWidth: 200 },
  statRule: { width: 1, alignSelf: 'stretch', backgroundColor: INK },
  statLabel: { fontFamily: MONO, fontSize: 11, color: INK_MUTED, letterSpacing: 1.6, textTransform: 'uppercase' as any, textAlign: 'right', marginTop: 6 },

  nextBlock: { borderWidth: 1, borderColor: INK, padding: 32 },
  nextRow: { flexDirection: 'row-reverse', alignItems: 'flex-start', borderTopWidth: 1, borderTopColor: INK, paddingVertical: 22 } as any,
  nextT: { fontFamily: SERIF_BOLD, fontSize: 20, color: INK, letterSpacing: -0.3, textAlign: 'right' },
  nextD: { fontFamily: SANS, fontSize: 15, color: INK_SOFT, lineHeight: 24, textAlign: 'right' },

  ctaRow: { flexDirection: 'row-reverse', flexWrap: 'wrap', justifyContent: 'center', gap: 14 } as any,
  helperNote: { fontFamily: SANS, fontSize: 14, color: INK_MUTED, textAlign: 'center' },
});
